import React, { useState } from 'react';
import {
  FileText,
  Calendar,
  CheckCircle2,
  ShoppingCart,
  Copy,
  Download,
  Code2,
  Printer,
  ShieldAlert,
  ArrowRight,
  Check,
  CheckCheck
} from 'lucide-react';
import { EditorialProject } from '../../../../../types/EditorialProject.types';

const cn = (...args: any[]) => args.filter(Boolean).join(' ');

interface Phase3Props {
  project: EditorialProject;
  onUpdateProject: (updated: EditorialProject) => void;
  onAdvanceToPhase4: (project: EditorialProject) => void;
}

export default function EditorialPhase3Production({
  project,
  onUpdateProject,
  onAdvanceToPhase4
}: Phase3Props) {
  const [activeTab, setActiveTab] = useState<'metadata' | 'escandallo' | 'gantt' | 'onix'>('metadata');
  const [copiedOnix, setCopiedOnix] = useState(false);

  // Regla 2: el ISBN y la ficha técnica quedan bloqueados sin contrato firmado
  const isContractSigned = project.contractDetails?.status === 'signed';
  // Regla 3: no se puede emitir la OT sin firma del Imprimátur
  const isImprimaturSigned = !!project.imprimatur?.approved;

  const [techSheet, setTechSheet] = useState({
    subtitle: project.technicalSheet?.subtitle || '',
    translator: project.technicalSheet?.translator || 'N/A',
    illustrator: project.technicalSheet?.illustrator || 'N/A',
    isbnPrint: project.technicalSheet?.isbnPrint || '978-84-15982-10-4',
    isbnDigital: project.technicalSheet?.isbnDigital || '978-84-15982-11-1',
    legalDeposit: project.technicalSheet?.legalDeposit || 'M-14892-2026',
    formatSize: project.technicalSheet?.formatSize || '17 x 24 cm',
    pageCount: project.technicalSheet?.pageCount || 480,
    paperType: project.technicalSheet?.paperType || 'Coral Book Ahuesado 80g',
    bindingType: project.technicalSheet?.bindingType || 'Rústica con solapas cosida con hilo',
    coverLamination: project.technicalSheet?.coverLamination || 'Plastificado mate con reservas brillo UVI'
  });

  const albatrossIsbnPool = [
    '978-84-15982-10-4',
    '978-84-15982-11-1',
    '978-84-15982-16-6',
    '978-84-15982-17-3',
    '978-84-15982-20-3',
    '978-84-15982-21-0'
  ];

  const [imprimaturNotes, setImprimaturNotes] = useState(project.imprimatur?.versionNotes || 'Galerada definitiva V2 revisada.');
  const [imprimaturSigner, setImprimaturSigner] = useState(project.imprimatur?.signedBy || 'Pedro Sánchez (Director)');

  const handleSaveTechSheet = () => onUpdateProject({ ...project, technicalSheet: { ...techSheet } });

  const handleSignImprimatur = () => {
    onUpdateProject({
      ...project,
      imprimatur: { approved: true, signedBy: imprimaturSigner, signedDate: new Date().toISOString().split('T')[0], versionNotes: imprimaturNotes }
    });
  };

  const handleGeneratePurchaseOrder = (category: 'service' | 'industrial', itemId: string) => {
    if (!project.costEscandallo) return;
    const updatedEscandallo = { ...project.costEscandallo };
    if (category === 'service') {
      updatedEscandallo.services = updatedEscandallo.services.map(s => (s.id === itemId ? { ...s, purchaseOrderGenerated: true } : s));
    } else {
      updatedEscandallo.industrials = updatedEscandallo.industrials.map(i => (i.id === itemId ? { ...i, purchaseOrderGenerated: true } : i));
    }
    onUpdateProject({ ...project, costEscandallo: updatedEscandallo });
  };

  const generateOnixXml = () => `<?xml version="1.0" encoding="UTF-8"?>
<ONIXMessage release="3.0" xmlns="http://ns.editeur.org/onix/3.0/reference">
  <Header>
    <Sender>
      <SenderName>Albatross Publishing Group S.L.</SenderName>
      <ContactName>Departamento Editorial</ContactName>
      <EmailAddress>editorial@albatross.com</EmailAddress>
    </Sender>
    <SentDateTime>${new Date().toISOString().replace(/[-:]/g, '').split('.')[0]}</SentDateTime>
    <MessageNote>Ficha Técnica Estándar ONIX 3.0 para Distribución Comercial</MessageNote>
  </Header>
  <Product>
    <RecordReference>${project.id}</RecordReference>
    <NotificationType>03</NotificationType>
    <RecordSourceType>01</RecordSourceType>
    <ProductIdentifier>
      <ProductIDType>15</ProductIDType>
      <IDValue>${techSheet.isbnPrint.replace(/-/g, '')}</IDValue>
    </ProductIdentifier>
    <DescriptiveDetail>
      <ProductComposition>00</ProductComposition>
      <ProductForm>BC</ProductForm>
      <TitleDetail>
        <TitleType>01</TitleType>
        <TitleElement>
          <TitleElementLevel>01</TitleElementLevel>
          <TitleText>${project.bookTitle}</TitleText>
          <Subtitle>${techSheet.subtitle || ''}</Subtitle>
        </TitleElement>
      </TitleDetail>
      <Contributor>
        <SequenceNumber>1</SequenceNumber>
        <ContributorRole>A01</ContributorRole>
        <PersonName>${project.bookAuthor}</PersonName>
      </Contributor>
      <Language>
        <LanguageRole>01</LanguageRole>
        <LanguageCode>spa</LanguageCode>
      </Language>
      <Extent>
        <ExtentType>00</ExtentType>
        <ExtentValue>${techSheet.pageCount}</ExtentValue>
        <ExtentUnit>03</ExtentUnit>
      </Extent>
      <Subject>
        <SubjectSchemeIdentifier>10</SubjectSchemeIdentifier>
        <SubjectHeadingText>${project.bookCategory}</SubjectHeadingText>
      </Subject>
    </DescriptiveDetail>
    <PublishingDetail>
      <Imprint>
        <ImprintName>Albatross Ediciones</ImprintName>
      </Imprint>
      <Publisher>
        <PublishingRole>01</PublishingRole>
        <PublisherName>Albatross Group S.L.</PublisherName>
      </Publisher>
      <PublishingStatus>04</PublishingStatus>
      <PublishingDate>
        <PublishingDateRole>01</PublishingDateRole>
        <Date>${project.targetReleaseDate.replace(/-/g, '')}</Date>
      </PublishingDate>
    </PublishingDetail>
    <SupplyDetail>
      <Price>
        <PriceType>02</PriceType>
        <PriceAmount>${project.formats[0]?.price || 22.00}</PriceAmount>
        <CurrencyCode>EUR</CurrencyCode>
      </Price>
    </SupplyDetail>
  </Product>
</ONIXMessage>`;

  const copyToClipboard = () => {
    navigator.clipboard?.writeText(generateOnixXml());
    setCopiedOnix(true);
    setTimeout(() => setCopiedOnix(false), 2000);
  };

  const downloadXmlFile = () => {
    const element = document.createElement('a');
    const file = new Blob([generateOnixXml()], { type: 'text/xml' });
    element.href = URL.createObjectURL(file);
    element.download = `ONIX_3.0_${project.bookTitle.replace(/\s+/g, '_')}.xml`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const servicesBudgeted = project.costEscandallo?.services.reduce((acc, s) => acc + s.budgeted, 0) || 0;
  const servicesActual = project.costEscandallo?.services.reduce((acc, s) => acc + s.actual, 0) || 0;
  const industrialsBudgeted = project.costEscandallo?.industrials.reduce((acc, i) => acc + i.budgeted, 0) || 0;
  const industrialsActual = project.costEscandallo?.industrials.reduce((acc, i) => acc + i.actual, 0) || 0;
  const totalCostBudgeted = servicesBudgeted + industrialsBudgeted;
  const totalCostActual = servicesActual + industrialsActual;

  return (
    <div className="ed-col-stack" style={{ gap: '1.5rem' }}>
      {!isContractSigned && (
        <div className="ed-alert ed-alert--danger" style={{ padding: '1rem' }}>
          <ShieldAlert size={18} />
          <div>
            <strong style={{ fontSize: '0.75rem' }}>Bloqueo de Seguridad Activo: Contrato de Derechos No Firmado</strong>
            El ISBN y la Ficha Técnica quedan estrictamente bloqueados si no existe Contrato de Derechos firmado en la Fase 2.
            Regrese a la Fase 2 y formalice la firma DocuSign para autorizar el uso de los bloques ISBN oficiales de Albatross.
          </div>
        </div>
      )}

      <div className="ed-tabs ed-tabs--plain">
        <button onClick={() => setActiveTab('metadata')} className={cn('ed-tab', activeTab === 'metadata' && 'ed-tab--active')}><FileText size={13} /> 4.1 Ficha Técnica &amp; Metadatos</button>
        <button onClick={() => setActiveTab('escandallo')} className={cn('ed-tab', activeTab === 'escandallo' && 'ed-tab--active')}><ShoppingCart size={13} /> 4.2 Escandallo de Costes</button>
        <button onClick={() => setActiveTab('gantt')} className={cn('ed-tab', activeTab === 'gantt' && 'ed-tab--active')}><Calendar size={13} /> 4.3 Cronograma Gantt &amp; Imprimátur</button>
        <button onClick={() => setActiveTab('onix')} className={cn('ed-tab', activeTab === 'onix' && 'ed-tab--active')}><Code2 size={13} /> Metadatos ONIX 3.0 (XML)</button>
      </div>

      {activeTab === 'metadata' && (
        <div className="ed-grid-12">
          <div className="ed-col-8">
            <div className="ed-panel">
              <div className="ed-panel-header">
                <h3 className="ed-panel-title"><FileText size={14} style={{ color: 'var(--color-brand-green)' }} /> Ficha_Técnica_Única (Especificaciones Industriales)</h3>
                <button disabled={!isContractSigned} onClick={handleSaveTechSheet} className="ed-btn ed-btn--primary"><Check size={12} /> Guardar Ficha</button>
              </div>

              <div className="ed-col-stack">
                <div className="ed-grid-2">
                  <div className="ed-field">
                    <label className="ed-field-label">Título Definitivo de la Obra</label>
                    <input type="text" readOnly value={project.bookTitle} className="ed-input ed-input--dark" style={{ cursor: 'not-allowed', opacity: 0.9 }} />
                  </div>
                  <div className="ed-field">
                    <label className="ed-field-label">Subtítulo o Mención de Edición</label>
                    <input type="text" disabled={!isContractSigned} value={techSheet.subtitle} onChange={(e) => setTechSheet({ ...techSheet, subtitle: e.target.value })} placeholder="Ej. Edición comentada y anotada" className="ed-input ed-input--dark" />
                  </div>
                </div>

                <div className="ed-grid-3">
                  <div className="ed-field">
                    <label className="ed-field-label">Autor Principal</label>
                    <input type="text" readOnly value={project.bookAuthor} className="ed-input ed-input--dark" style={{ opacity: 0.9 }} />
                  </div>
                  <div className="ed-field">
                    <label className="ed-field-label">Traductor / Adaptador</label>
                    <input type="text" disabled={!isContractSigned} value={techSheet.translator} onChange={(e) => setTechSheet({ ...techSheet, translator: e.target.value })} className="ed-input ed-input--dark" />
                  </div>
                  <div className="ed-field">
                    <label className="ed-field-label">Ilustrador / Portadista</label>
                    <input type="text" disabled={!isContractSigned} value={techSheet.illustrator} onChange={(e) => setTechSheet({ ...techSheet, illustrator: e.target.value })} className="ed-input ed-input--dark" />
                  </div>
                </div>

                <div className="ed-box" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  <span style={{ fontSize: '0.5625rem', fontWeight: 700, color: 'var(--color-brand-green)', textTransform: 'uppercase' }}>Especificaciones Físicas e Industriales</span>
                  <div className="ed-grid-3">
                    <div className="ed-field">
                      <label className="ed-field-label">Formato / Tamaño</label>
                      <input type="text" disabled={!isContractSigned} value={techSheet.formatSize} onChange={(e) => setTechSheet({ ...techSheet, formatSize: e.target.value })} className="ed-input" />
                    </div>
                    <div className="ed-field">
                      <label className="ed-field-label">Nº de Páginas</label>
                      <input type="number" disabled={!isContractSigned} value={techSheet.pageCount} onChange={(e) => setTechSheet({ ...techSheet, pageCount: Number(e.target.value) })} className="ed-input" />
                    </div>
                    <div className="ed-field">
                      <label className="ed-field-label">Tipo de Papel Interior</label>
                      <input type="text" disabled={!isContractSigned} value={techSheet.paperType} onChange={(e) => setTechSheet({ ...techSheet, paperType: e.target.value })} className="ed-input" />
                    </div>
                    <div className="ed-field">
                      <label className="ed-field-label">Tipo Encuadernación</label>
                      <input type="text" disabled={!isContractSigned} value={techSheet.bindingType} onChange={(e) => setTechSheet({ ...techSheet, bindingType: e.target.value })} className="ed-input" />
                    </div>
                  </div>
                  <div className="ed-field">
                    <label className="ed-field-label">Plastificado / Acabado Cubierta</label>
                    <input type="text" disabled={!isContractSigned} value={techSheet.coverLamination} onChange={(e) => setTechSheet({ ...techSheet, coverLamination: e.target.value })} className="ed-input" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="ed-col-4">
            <div className="ed-panel">
              <h3 className="ed-panel-title" style={{ marginBottom: '1rem', borderBottom: '1px solid var(--color-brand-blue-lighter)', paddingBottom: '0.75rem' }}>
                <Code2 size={14} style={{ color: '#38bdf8' }} /> Bloque_ISBN &amp; Legal (Albatross API)
              </h3>
              <div className="ed-col-stack">
                <div className="ed-box">
                  <span className="ed-label">ISBN Versión Impresa (Papel)</span>
                  <input type="text" disabled={!isContractSigned} value={techSheet.isbnPrint} onChange={(e) => setTechSheet({ ...techSheet, isbnPrint: e.target.value })} className="ed-input" style={{ fontWeight: 700 }} />
                </div>
                <div className="ed-box">
                  <span className="ed-label">ISBN Versión Digital (ePub/Mobi)</span>
                  <input type="text" disabled={!isContractSigned} value={techSheet.isbnDigital} onChange={(e) => setTechSheet({ ...techSheet, isbnDigital: e.target.value })} className="ed-input" style={{ fontWeight: 700 }} />
                </div>
                <div className="ed-box">
                  <span className="ed-label">Depósito Legal</span>
                  <input type="text" disabled={!isContractSigned} value={techSheet.legalDeposit} onChange={(e) => setTechSheet({ ...techSheet, legalDeposit: e.target.value })} className="ed-input" />
                </div>
                <div className="ed-connector">
                  <span style={{ fontSize: '0.5rem', textTransform: 'uppercase', color: '#38bdf8', fontWeight: 700 }}>Bloques ISBN precargados en Albatross:</span>
                  <div className="ed-col-stack" style={{ gap: '0.25rem' }}>
                    {albatrossIsbnPool.map((isbn) => (
                      <div key={isbn} className="ed-row-between" style={{ fontSize: '0.5rem', color: '#cbd5e1' }}>
                        <span>{isbn}</span>
                        <button disabled={!isContractSigned} onClick={() => setTechSheet({ ...techSheet, isbnPrint: isbn })} style={{ background: 'none', border: 'none', color: 'var(--color-brand-green)', textTransform: 'uppercase', cursor: 'pointer' }}>Asignar</button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'escandallo' && (
        <div className="ed-col-stack" style={{ gap: '1.25rem' }}>
          <div className="ed-grid-3">
            <div className="ed-panel">
              <span className="ed-label">Costes Directos Presupuestados</span>
              <span style={{ fontSize: '1.125rem', fontWeight: 700, color: '#fff', display: 'block', marginTop: '0.25rem' }}>{totalCostBudgeted.toLocaleString()} €</span>
            </div>
            <div className="ed-panel">
              <span className="ed-label">Costes Reales Comprometidos / Facturados</span>
              <span style={{ fontSize: '1.125rem', fontWeight: 700, color: '#38bdf8', display: 'block', marginTop: '0.25rem' }}>{totalCostActual.toLocaleString()} €</span>
            </div>
            <div className="ed-panel">
              <span className="ed-label">Desviación Total Escandallo</span>
              <span style={{ fontSize: '1.125rem', fontWeight: 700, display: 'block', marginTop: '0.25rem', color: totalCostBudgeted - totalCostActual >= 0 ? 'var(--color-brand-green)' : '#f87171' }}>
                {(totalCostBudgeted - totalCostActual >= 0 ? '+' : '') + (totalCostBudgeted - totalCostActual).toLocaleString()} €
              </span>
            </div>
          </div>

          <div className="ed-grid-2">
            <div className="ed-panel">
              <h3 className="ed-panel-title" style={{ marginBottom: '1rem', borderBottom: '1px solid var(--color-brand-blue-lighter)', paddingBottom: '0.75rem' }}>
                <FileText size={14} style={{ color: '#38bdf8' }} /> Servicios Editoriales
              </h3>
              <div className="ed-col-stack">
                {project.costEscandallo?.services.map((item) => (
                  <div key={item.id} className="ed-box">
                    <div className="ed-row-between" style={{ alignItems: 'flex-start' }}>
                      <div>
                        <strong style={{ color: '#fff', display: 'block' }}>{item.concept}</strong>
                        <span style={{ fontSize: '0.5rem', color: '#94a3b8', display: 'block', marginTop: '0.125rem' }}>Acreedor: {item.provider}</span>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <span className="ed-value">{item.budgeted.toLocaleString()} €</span>
                        <span style={{ fontSize: '0.5rem', color: '#94a3b8', display: 'block' }}>Real: {item.actual.toLocaleString()} €</span>
                      </div>
                    </div>
                    <div className="ed-row-between" style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid var(--color-brand-blue-lighter)' }}>
                      <span className={cn('ed-badge', item.purchaseOrderGenerated ? 'ed-badge--green' : 'ed-badge--amber')}>
                        {item.purchaseOrderGenerated ? '✓ Orden de Compra Generada' : 'Pendiente O.C.'}
                      </span>
                      {!item.purchaseOrderGenerated && (
                        <button onClick={() => handleGeneratePurchaseOrder('service', item.id)} className="ed-badge ed-badge--sky" style={{ cursor: 'pointer' }}>Generar OC Servicios</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="ed-panel">
              <h3 className="ed-panel-title" style={{ marginBottom: '1rem', borderBottom: '1px solid var(--color-brand-blue-lighter)', paddingBottom: '0.75rem' }}>
                <Printer size={14} style={{ color: 'var(--color-brand-green)' }} /> Costes Industriales
              </h3>
              <div className="ed-col-stack">
                {project.costEscandallo?.industrials.map((item) => (
                  <div key={item.id} className="ed-box">
                    <div className="ed-row-between" style={{ alignItems: 'flex-start' }}>
                      <div>
                        <strong style={{ color: '#fff', display: 'block' }}>{item.concept}</strong>
                        <span style={{ fontSize: '0.5rem', color: '#94a3b8', display: 'block', marginTop: '0.125rem' }}>Industrial: {item.provider}</span>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <span className="ed-value">{item.budgeted.toLocaleString()} €</span>
                        <span style={{ fontSize: '0.5rem', color: '#94a3b8', display: 'block' }}>Real: {item.actual.toLocaleString()} €</span>
                      </div>
                    </div>
                    <div className="ed-row-between" style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid var(--color-brand-blue-lighter)' }}>
                      <span className={cn('ed-badge', item.purchaseOrderGenerated ? 'ed-badge--green' : 'ed-badge--amber')}>
                        {item.purchaseOrderGenerated ? '✓ Orden de Compra Generada' : 'Pendiente O.C.'}
                      </span>
                      {!item.purchaseOrderGenerated && (
                        <button onClick={() => handleGeneratePurchaseOrder('industrial', item.id)} className="ed-badge ed-badge--green" style={{ cursor: 'pointer' }}>Generar OC Industrial</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'gantt' && (
        <div className="ed-grid-12">
          <div className="ed-col-8">
            <div className="ed-panel">
              <h3 className="ed-panel-title" style={{ marginBottom: '1rem', borderBottom: '1px solid var(--color-brand-blue-lighter)', paddingBottom: '0.75rem' }}>
                <Calendar size={14} style={{ color: 'var(--color-brand-green)' }} /> Cronograma_Gantt_Editorial (Hitos con Dependencias)
              </h3>
              <div className="ed-col-stack">
                {project.ganttSchedule?.map((task) => (
                  <div key={task.id} className="ed-box">
                    <div className="ed-row-between" style={{ marginBottom: '0.375rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {task.isImprimatur && <span className="ed-badge ed-badge--amber">HITO CRÍTICO</span>}
                        <strong style={{ color: '#fff' }}>{task.taskName}</strong>
                      </div>
                      <span style={{ fontSize: '0.5625rem', color: '#94a3b8' }}>Responsable: <strong style={{ color: '#38bdf8' }}>{task.assignee}</strong></span>
                    </div>
                    <div className="ed-row-between" style={{ fontSize: '0.5rem', color: '#94a3b8', marginBottom: '0.5rem' }}>
                      <span>Inicio: {task.startDate}</span>
                      <span>Fin: {task.endDate}</span>
                      <span>Progreso: <strong style={{ color: '#fff' }}>{task.progressPct}%</strong></span>
                    </div>
                    <div className="ed-progress-track">
                      <div className={cn('ed-progress-fill', task.progressPct === 100 ? 'ed-progress-fill--done' : task.progressPct > 0 ? 'ed-progress-fill--active' : '')} style={{ width: `${task.progressPct}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="ed-col-4">
            <div className="ed-panel">
              <h3 className="ed-panel-title" style={{ marginBottom: '1rem', borderBottom: '1px solid var(--color-brand-blue-lighter)', paddingBottom: '0.75rem' }}>
                <CheckCheck size={14} style={{ color: '#fbbf24' }} /> Imprimátur (OK_A_PRUEBAS)
              </h3>
              <div className="ed-col-stack">
                <div className="ed-box">
                  <span className="ed-label" style={{ marginBottom: '0.25rem' }}>Estado de Galeradas / Pruebas</span>
                  <div className="ed-row-between">
                    <span className={cn('ed-badge', isImprimaturSigned ? 'ed-badge--green' : 'ed-badge--amber')}>{isImprimaturSigned ? 'IMPRIMÁTUR FIRMADO' : 'PRUEBAS EN REVISIÓN'}</span>
                    {project.imprimatur?.signedDate && <span style={{ fontSize: '0.5rem', color: '#94a3b8' }}>{project.imprimatur.signedDate}</span>}
                  </div>
                </div>
                <div className="ed-field">
                  <label className="ed-field-label">Firmante Responsable</label>
                  <input type="text" value={imprimaturSigner} onChange={(e) => setImprimaturSigner(e.target.value)} className="ed-input ed-input--dark" />
                </div>
                <div className="ed-field">
                  <label className="ed-field-label">Observaciones de la Maqueta Final</label>
                  <textarea rows={3} value={imprimaturNotes} onChange={(e) => setImprimaturNotes(e.target.value)} className="ed-textarea ed-textarea--dark" />
                </div>

                {!isImprimaturSigned ? (
                  <div className="ed-alert ed-alert--danger">
                    <ShieldAlert size={16} />
                    <div><strong>Control de Bloqueo Fase 3:</strong> No se puede emitir la Orden de Impresión (OT) a la imprenta sin la firma del Imprimátur (OK a Pruebas).</div>
                  </div>
                ) : (
                  <div className="ed-alert ed-alert--success">
                    <CheckCircle2 size={16} />
                    <span>Imprimátur validado por {project.imprimatur?.signedBy}. Habilitada emisión de Orden de Impresión en Fase 4.</span>
                  </div>
                )}

                <button type="button" onClick={handleSignImprimatur} className="ed-btn ed-btn--amber ed-btn--block">
                  <CheckCheck size={14} /> {isImprimaturSigned ? 'Actualizar Firma de Imprimátur' : 'Firmar Imprimátur Definitivo'}
                </button>

                <div style={{ paddingTop: '0.5rem', borderTop: '1px solid var(--color-brand-blue-lighter)' }}>
                  <button type="button" disabled={!isImprimaturSigned} onClick={() => onAdvanceToPhase4(project)} className="ed-btn ed-btn--primary ed-btn--block">
                    <ArrowRight size={14} /> Avanzar a Fase 4: Cierre e Impresión
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'onix' && (
        <div className="ed-panel">
          <div className="ed-panel-header">
            <div>
              <h3 className="ed-panel-title"><Code2 size={14} style={{ color: 'var(--color-brand-green)' }} /> Estándar_ONIX_3.0 (DILVE, Librerías y Plataformas)</h3>
              <p className="ed-panel-sub">Generador sintáctico automático basado en los metadatos de la obra.</p>
            </div>
            <div className="ed-btn-row">
              <button onClick={copyToClipboard} className="ed-btn ed-btn--outline"><Copy size={12} /> {copiedOnix ? 'Copiado!' : 'Copiar XML'}</button>
              <button onClick={downloadXmlFile} className="ed-btn ed-btn--primary"><Download size={12} /> Descargar Archivo ONIX (.xml)</button>
            </div>
          </div>
          <pre className="ed-pre">{generateOnixXml()}</pre>
        </div>
      )}
    </div>
  );
}
